import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Target, ArrowRight, Loader2 } from "lucide-react";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";

const HERO_BG =
  "https://images.unsplash.com/photo-1487958449943-2429e8be8625?auto=format&fit=crop&w=1600&q=80";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const err = params.get("google_error");
    if (err) {
      const messages = {
        state_mismatch: "Google sign-in expired mid-flow. Please try again.",
        token_exchange_failed: "Google didn't accept the sign-in — please retry.",
        userinfo_failed: "Couldn't fetch your Google profile — please retry.",
        incomplete_profile: "Google returned an incomplete profile — please retry.",
        access_denied: "You cancelled the Google sign-in.",
      };
      toast.error(messages[err] || `Google sign-in failed: ${err}`);
      params.delete("google_error");
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = await login(email.trim(), password);
    setBusy(false);
    if (r.ok) {
      if (r.user && r.user.email_verified === false) {
        // Don't toast "Welcome back" — surface the verification block immediately
        // and route straight to the inbox-check page (which has the Resend CTA).
        toast.info("Please verify your email to continue. Check your inbox for the link.");
        nav("/check-inbox", { replace: true });
      } else {
        toast.success("Welcome back");
        nav("/dashboard", { replace: true });
      }
    } else {
      toast.error(r.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-ink-50">
      {/* form */}
      <div className="flex flex-col justify-center px-6 sm:px-12 py-16 bg-white">
        <div className="max-w-md w-full mx-auto">
          <div className="flex items-center gap-3 mb-12">
            <img
              src="/logo-128.png"
              alt="LeadIQ Pro"
              width="48"
              height="48"
              className="w-12 h-12 shrink-0"
            />
            <div className="leading-tight">
              <div className="font-display font-extrabold text-lg tracking-tight text-ink-900">
                LeadIQ Pro
              </div>
              <div className="text-[10px] uppercase tracking-widestx text-ink-400 font-mono">
                Lead Enrichment Engine
              </div>
            </div>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-none font-extrabold text-ink-900 mb-3">
            Sign in.
          </h1>
          <p className="text-ink-500 text-sm mb-10 max-w-sm">
            Transform basic contacts into enriched intelligence packages with web-grounded AI.
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <GoogleSignInButton testid="login-google-button" />
            <div className="flex items-center gap-3 text-[10px] uppercase tracking-widestx text-ink-400 font-mono">
              <span className="h-px bg-ink-100 flex-1" />
              or continue with email
              <span className="h-px bg-ink-100 flex-1" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500">
                Work email
              </Label>
              <Input
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="mt-2 rounded-sm border-ink-200 focus:border-brand focus-visible:ring-brand h-11 font-mono text-sm"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2 mt-2">
                <Label className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500">
                  Password
                </Label>
                <Link
                  to="/forgot-password"
                  data-testid="forgot-password-link"
                  className="text-[10px] uppercase tracking-widestx font-semibold text-brand hover:text-brand-hover"
                >
                  Forgot?
                </Link>
              </div>
              <Input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="rounded-sm border-ink-200 focus:border-brand focus-visible:ring-brand h-11"
              />
            </div>
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={busy}
              className="w-full h-11 rounded-sm bg-brand hover:bg-brand-hover text-white font-medium tracking-wide group"
            >
              {busy ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Sign in
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-8 text-sm text-ink-500">
            New to LeadIQ?{" "}
            <Link to="/register" data-testid="go-to-register-link" className="text-brand hover:text-brand-hover font-medium underline-offset-4 hover:underline">
              Create an account
            </Link>
          </div>
        </div>
      </div>

      {/* hero */}
      <div className="hidden lg:block relative overflow-hidden">
        <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-900/80 via-ink-900/30 to-ink-900/10" />
        <div className="relative h-full flex flex-col justify-end p-12">
          <div className="max-w-md">
            <div className="text-[10px] uppercase tracking-widestx font-mono text-white/80 mb-4">
              v1.0 — beta
            </div>
            <h2 className="font-display text-4xl font-light text-white leading-tight tracking-tight">
              Every contact,<br />
              <span className="font-extrabold">a complete intelligence package.</span>
            </h2>
            <p className="text-white/80 text-sm mt-6 max-w-sm leading-relaxed">
              Tech stack discovery. Hiring signals. Buying committees. Engagement playbooks. All from a single CSV.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
